import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch, RootState } from '../../../store';
import { logger } from '../../../utils/logger';
import {
  selectCurrentExplanation,
  getAdaptiveExplanation,
  adaptExplanation,
  updateExplanationLevel,
} from '../../../store/slices/aiTutoringSlice';

interface AdaptiveExplanationCardProps {
  onRequestExplanation: (topicId: string) => void;
  loading: boolean;
}

const AdaptiveExplanationCard: React.FC<AdaptiveExplanationCardProps> = ({
  onRequestExplanation,
  loading,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const currentExplanation = useSelector(selectCurrentExplanation);
  const [selectedTopic, setSelectedTopic] = useState<string>('brewing-basics');
  const [userFeedback, setUserFeedback] = useState<
    'understood' | 'confused' | 'bored' | null
  >(null);

  const topics = [
    { id: 'brewing-basics', name: 'Brewing Basics', difficulty: 'beginner' },
    { id: 'grind-size', name: 'Grind Size Impact', difficulty: 'intermediate' },
    {
      id: 'extraction-theory',
      name: 'Extraction Theory',
      difficulty: 'advanced',
    },
    { id: 'water-chemistry', name: 'Water Chemistry', difficulty: 'expert' },
  ];

  const handleRequestExplanation = async () => {
    try {
      await dispatch(
        getAdaptiveExplanation({
          topicId: selectedTopic,
          currentLevel: 'intermediate',
          userContext: {
            previousFeedback: userFeedback,
            learningStyle: 'visual',
          },
        })
      );
    } catch (error) {
      logger.error('Failed to get explanation:', error);
    }
  };

  const handleLevelAdjustment = (direction: 'up' | 'down') => {
    if (!currentExplanation) return;

    const currentLevel = currentExplanation.currentLevel;
    const maxLevel = currentExplanation.explanationLevels.length - 1;

    let newLevel = currentLevel;
    if (direction === 'up' && currentLevel < maxLevel) {
      newLevel = currentLevel + 1;
    } else if (direction === 'down' && currentLevel > 0) {
      newLevel = currentLevel - 1;
    }

    if (newLevel !== currentLevel) {
      dispatch(updateExplanationLevel(newLevel));
    }
  };

  const handleFeedback = async (
    feedback: 'understood' | 'confused' | 'bored'
  ) => {
    setUserFeedback(feedback);

    if (currentExplanation) {
      try {
        await dispatch(
          adaptExplanation({
            explanationId: currentExplanation.explanationId,
            adaptationType:
              feedback === 'confused'
                ? 'simplify'
                : feedback === 'bored'
                  ? 'increase_complexity'
                  : 'maintain',
            userFeedback: feedback,
          })
        );
      } catch (error) {
        logger.error('Failed to adapt explanation:', error);
      }
    }
  };

  const renderExplanationContent = () => {
    if (!currentExplanation) return null;

    const currentLevel =
      currentExplanation.explanationLevels[currentExplanation.currentLevel];
    if (!currentLevel) return null;

    return (
      <View style={styles.explanationContent}>
        <View style={styles.explanationHeader}>
          <Text style={styles.explanationTitle}>
            {currentExplanation.concept}
          </Text>
          <View style={styles.difficultyBadge}>
            <Text style={styles.difficultyText}>{currentLevel.difficulty}</Text>
          </View>
        </View>

        <ScrollView
          style={styles.contentScroll}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.explanationText}>
            {currentLevel.content.text}
          </Text>

          {/* Key Points */}
          {currentLevel.keyPoints.length > 0 && (
            <View style={styles.keyPointsSection}>
              <Text style={styles.sectionTitle}>Key Points</Text>
              {currentLevel.keyPoints.map((point, index) => (
                <View key={index} style={styles.keyPoint}>
                  <Icon name='check-circle' size={16} color='#4CAF50' />
                  <Text style={styles.keyPointText}>{point}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Examples */}
          {currentLevel.examples.length > 0 && (
            <View style={styles.examplesSection}>
              <Text style={styles.sectionTitle}>Examples</Text>
              {currentLevel.examples.map((example, index) => (
                <View key={example.exampleId} style={styles.exampleCard}>
                  <Text style={styles.exampleScenario}>{example.scenario}</Text>
                  <Text style={styles.exampleExplanation}>
                    {example.explanation}
                  </Text>
                </View>
              ))}
            </View>
          )}
        </ScrollView>

        {/* Level Controls */}
        <View style={styles.levelControls}>
          <TouchableOpacity
            style={[
              styles.levelButton,
              currentExplanation.currentLevel === 0 &&
                styles.levelButtonDisabled,
            ]}
            onPress={() => handleLevelAdjustment('down')}
            disabled={currentExplanation.currentLevel === 0}
          >
            <Icon name='remove' size={20} color='#666' />
            <Text style={styles.levelButtonText}>Simpler</Text>
          </TouchableOpacity>

          <View style={styles.levelIndicator}>
            <Text style={styles.levelText}>
              Level {currentExplanation.currentLevel + 1} of{' '}
              {currentExplanation.explanationLevels.length}
            </Text>
          </View>

          <TouchableOpacity
            style={[
              styles.levelButton,
              currentExplanation.currentLevel ===
                currentExplanation.explanationLevels.length - 1 &&
                styles.levelButtonDisabled,
            ]}
            onPress={() => handleLevelAdjustment('up')}
            disabled={
              currentExplanation.currentLevel ===
              currentExplanation.explanationLevels.length - 1
            }
          >
            <Icon name='add' size={20} color='#666' />
            <Text style={styles.levelButtonText}>Advanced</Text>
          </TouchableOpacity>
        </View>

        {/* Feedback Buttons */}
        <View style={styles.feedbackSection}>
          <Text style={styles.feedbackTitle}>How was this explanation?</Text>
          <View style={styles.feedbackButtons}>
            {[
              {
                key: 'understood',
                label: 'Clear',
                icon: 'thumb-up',
                color: '#4CAF50',
              },
              {
                key: 'confused',
                label: 'Confusing',
                icon: 'help',
                color: '#FF9800',
              },
              {
                key: 'bored',
                label: 'Too Simple',
                icon: 'trending-up',
                color: '#2196F3',
              },
            ].map(option => (
              <TouchableOpacity
                key={option.key}
                style={[
                  styles.feedbackButton,
                  userFeedback === option.key && {
                    backgroundColor: `${option.color}15`,
                  },
                ]}
                onPress={() => handleFeedback(option.key as any)}
              >
                <Icon
                  name={option.icon}
                  size={18}
                  color={userFeedback === option.key ? option.color : '#666'}
                />
                <Text
                  style={[
                    styles.feedbackButtonText,
                    userFeedback === option.key && { color: option.color },
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Adaptive Explanations</Text>
        <Text style={styles.subtitle}>
          AI-powered learning content that adapts to you
        </Text>
      </View>

      {!currentExplanation ? (
        <View style={styles.requestSection}>
          <Text style={styles.requestTitle}>
            Choose a topic to learn about:
          </Text>

          <View style={styles.topicsGrid}>
            {topics.map(topic => (
              <TouchableOpacity
                key={topic.id}
                style={[
                  styles.topicCard,
                  selectedTopic === topic.id && styles.selectedTopicCard,
                ]}
                onPress={() => setSelectedTopic(topic.id)}
              >
                <Text
                  style={[
                    styles.topicName,
                    selectedTopic === topic.id && styles.selectedTopicName,
                  ]}
                >
                  {topic.name}
                </Text>
                <Text style={styles.topicDifficulty}>{topic.difficulty}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            style={styles.requestButton}
            onPress={handleRequestExplanation}
            disabled={loading}
          >
            <Icon name='school' size={20} color='#fff' />
            <Text style={styles.requestButtonText}>
              {loading ? 'Generating...' : 'Get Explanation'}
            </Text>
          </TouchableOpacity>
        </View>
      ) : (
        renderExplanationContent()
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  header: {
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
  },
  requestSection: {
    alignItems: 'center',
  },
  requestTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1a1a1a',
    marginBottom: 16,
    textAlign: 'center',
  },
  topicsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 20,
    justifyContent: 'center',
  },
  topicCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    minWidth: '45%',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: 'transparent',
  },
  selectedTopicCard: {
    backgroundColor: '#e3f2fd',
    borderColor: '#2196F3',
  },
  topicName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    textAlign: 'center',
    marginBottom: 4,
  },
  selectedTopicName: {
    color: '#2196F3',
  },
  topicDifficulty: {
    fontSize: 12,
    color: '#666',
    textTransform: 'capitalize',
  },
  requestButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    gap: 8,
  },
  requestButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  explanationContent: {
    maxHeight: 500,
  },
  explanationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  explanationTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a1a',
    flex: 1,
  },
  difficultyBadge: {
    backgroundColor: '#e3f2fd',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  difficultyText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#2196F3',
    textTransform: 'capitalize',
  },
  contentScroll: {
    maxHeight: 200,
    marginBottom: 16,
  },
  explanationText: {
    fontSize: 16,
    lineHeight: 24,
    color: '#1a1a1a',
    marginBottom: 16,
  },
  keyPointsSection: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 8,
  },
  keyPoint: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  keyPointText: {
    fontSize: 14,
    color: '#1a1a1a',
    marginLeft: 8,
    flex: 1,
  },
  examplesSection: {
    marginBottom: 16,
  },
  exampleCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  exampleScenario: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: 4,
  },
  exampleExplanation: {
    fontSize: 14,
    color: '#666',
  },
  levelControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingVertical: 8,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e1e5e9',
  },
  levelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    gap: 4,
  },
  levelButtonDisabled: {
    opacity: 0.5,
  },
  levelButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  levelIndicator: {
    alignItems: 'center',
  },
  levelText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
  feedbackSection: {
    alignItems: 'center',
  },
  feedbackTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#1a1a1a',
    marginBottom: 12,
  },
  feedbackButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  feedbackButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    gap: 4,
  },
  feedbackButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666',
  },
});

export default AdaptiveExplanationCard;
